import {
  isOperation,
  isProblem,
  type Entity,
  type Operation,
} from "@kodall/kodall-client/dist/types";
import { intercept } from "~/service/kodall-client";

export type BackendNotification = {
  key: number;
  subject: string;
  body: string;
  link: string;
  date_created: string;

  keyNotificareStare: number;
  denumireStatus: string;
  labelStatus: string;

  typeStatus: string;
  priorityStatus: string;
};

// Global state
const notifications = ref<BackendNotification[]>([]);
const page = ref<number>(1);
let pollInterval: NodeJS.Timeout | null = null; // Singleton interval reference
export const useNotification = () => {
  const account = useAccount();
  const kc = useSecurity().kc.value;
  const notifStatusMap: Map<string, number> = new Map();
  const toast = useToast(); // Nuxt UI Toast

  // nu ne trebuie

  // const getStatusPriority = async () => {
  //   return await intercept<{key: number, name: string}[]>(() =>
  //     kc.fetch<{key: number, name: string}>(`
  //       FETCH notification_priority (key, name, label)
  //     `)
  //   );
  // };
  // const getStatusType = async () => {
  //   return await intercept<{key: number, name: string}[]>(() =>
  //     kc.fetch<{key: number, name: string}>(`
  //       FETCH notification_type (key, name, label)
  //     `)
  //   );
  // };

  // --- 1. Existing Fetch Logic ---
  const getAllNotifications = async (
    limit: number = 100
  ): Promise<BackendNotification[]> => {
    const all: BackendNotification[] = [];

    while (true) {
      const batch = await getPaginatedNotifications(page.value, limit);
      if (batch.length === 0) {
        break; 
      }
      all.push(...batch);
      page.value++;
    }
    return all;
  };

  const getPaginatedNotifications = async (page: number, limit: number = 100): Promise<BackendNotification[]> => {
    return await intercept<BackendNotification[]>(() =>
      kc.fetch<BackendNotification>(`
        FETCH n_notification_user (key, subject, body, link, date_created, type_name AS typeStatus, priority_name AS priorityStatus){
          n_notification_status TO id_n_notification_status (key AS keyNotificareStare, label AS labelStatus, name AS denumireStatus)
        }
         ORDER BY key DESC 
         LIMIT ${(page - 1) * limit}, ${limit}
        `)
    );
  };

  // --- 2. New Logic: Fetch only items newer than the last one we have ---
  const fetchNewNotifications = async (
    lastKnownKey: number
  ): Promise<BackendNotification[]> => {
    // We filter for keys GREATER than the last one we saw
    const result = await intercept<BackendNotification[]>(() =>
      kc.fetch<BackendNotification>(`
        FETCH n_notification_user (key, subject, body, link, date_created, type_name AS typeStatus, priority_name AS priorityStatus){
          n_notification_status TO id_n_notification_status (key AS keyNotificareStare, label AS labelStatus, name AS denumireStatus)
        } FILTER AND (key > ${lastKnownKey}, web_sent == 1)
        ORDER BY key DESC
      `)
    );
    return result || [];
  };

  // --- 3. The Polling Logic ---
  const checkNewNotifications = async () => {
    // Condition: Check if ch_web is active
    const isWebActive =
      account.account.value?.ch_web === true ||
      account.account.value?.ch_web === 1;
    if (!isWebActive) return;

    // Get the highest key currently in our list to determine what is "New"
    const lastKey =
      notifications.value.length > 0
        ? Math.max(...notifications.value.map((n) => n.key))
        : 0;

    const newItems = await fetchNewNotifications(lastKey);

    if (newItems.length > 0) {
      // Add new items to the BEGINNING of the list (since we sort DESC)
      notifications.value.unshift(...newItems);

      // Filter only the unread ones to show toasts
      const unreadNew = newItems.filter((n) => n.labelStatus === "necitit");

      unreadNew.forEach((n) => {
        toast.add({
          title: "Notificare Nouă",
          description: n.subject,
          icon: "i-heroicons-bell-alert",
          color: "primary",
          duration: 10000,
          type: "foreground",
        });
      });
    }
  };

  const startPolling = () => {
    if (pollInterval) return; // Prevent duplicate intervals

    // Run immediately once, then set interval
    checkNewNotifications();

    pollInterval = setInterval(async () => {
      await checkNewNotifications();
    }, 5000); // 5 Seconds
  };

  const stopPolling = () => {
    if (pollInterval) {
      clearInterval(pollInterval);
      pollInterval = null;
    }
  };

  // --- Standard Logic ---
  const changeNotificationLabel = async (key: number, labelStare: string) => {
    const notif = await kc.get("n_notification_user", key);
    if (isProblem(notif)) return null;

    let keyNotificareStare = notifStatusMap.get(labelStare);
    notif.properties.id_n_notification_status = keyNotificareStare;
    var result = await kc.update(notif);

    if (!isOperation(result)) return null;

    var updatedNotif = await getNotification(key);
    if (!updatedNotif) return null;

    const existing = notifications.value.find((n) => n.key === key);
    if (existing) {
      Object.assign(existing, updatedNotif);
    }
  };

  const getAllNotificationStatuses = async (): Promise<NomenclatorItem[]> => {
    return await intercept<NomenclatorItem[]>(() =>
      kc.fetch<NomenclatorItem>(`
        FETCH n_notification_status (key AS id, label)
      `)
    );
  };

  const constructMap = async () => {
    const statuses = await getAllNotificationStatuses();
    for (const status of statuses) {
      notifStatusMap.set(status.label, status.id);
    }
  };

  const constructNotifications = async () => {
    // Only fetch full list if empty (or force refresh)
    if (notifications.value.length === 0) {
      notifications.value = await getAllNotifications(10);
    }
  };

  const getNotification = async (
    notificationKey: number
  ): Promise<BackendNotification | null> => {
    var result = await intercept<BackendNotification[]>(() =>
      kc.fetch<BackendNotification>(`
        FETCH n_notification_user (key, subject, body, link, date_created, type_name AS typeStatus, priority_name AS priorityStatus){
          n_notification_status TO id_n_notification_status (key AS keyNotificareStare, label AS labelStatus, name AS denumireStatus)
        } FILTER AND (key == ${notificationKey})
         LIMIT 1
        `)
    );

    if (!result[0]) return null;
    return result[0];
  };

  const markAllAsRead = async () => {
    const unReadNotifications = notifications.value.filter(
      (n) => n.labelStatus === "necitit"
    );
    for (const notification of unReadNotifications) {
      await changeNotificationLabel(notification.key, "citit");
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  };

  // --- Initialization ---

  constructMap();
  constructNotifications().then(() => {
    startPolling();
  });

  watch(
    () => account.account.value?.ch_web,
    (newVal) => {
      if (notifications.value.length === 0) return;
      if (newVal === true || newVal === 1) {
        startPolling();
      } else {
        stopPolling();
      }
    }
  );

  return {
    getAllNotifications,
    changeNotificationLabel,
    notifications,
    markAllAsRead,
    // getStatusPriority,
    // getStatusType,
  };
};
